import React, { useMemo, useState } from 'react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ChevronUpIcon, ChevronDownIcon, MoreHorizontalIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export type BulkPaymentStatus = 'draft' | 'processing' | 'completed' | 'failed';
export type PaymentStatus = 'pending' | 'paid' | 'expired' | 'failed' | 'cancelled';
export interface PaymentItem {
  id: string;
  customerName: string;
  email: string;
  phone: string;
  amount: string;
  currency: string;
  description: string;
  reference: string;
  status: PaymentStatus;
  dueDate?: string;
}
export interface BulkPaymentBatch {
  id: string;
  name: string;
  createdAt: string;
  totalAmount: number;
  currency: string;
  totalItems: number;
  completedItems: number;
  status: BulkPaymentStatus;
  collectionRate: number;
}
const MOCK_BATCHES: BulkPaymentBatch[] = [{
  id: '1',
  name: 'December Invoices',
  createdAt: '2023-12-15T09:30:00',
  totalAmount: 12450.75,
  currency: getTenant().currency.code,
  totalItems: 45,
  completedItems: 38,
  status: 'completed',
  collectionRate: 84.4
}, {
  id: '2',
  name: 'Q1 Subscriptions',
  createdAt: '2023-12-01T14:15:00',
  totalAmount: 8750.0,
  currency: getTenant().currency.code,
  totalItems: 25,
  completedItems: 23,
  status: 'completed',
  collectionRate: 92.0
}, {
  id: '3',
  name: 'Event Tickets - Summer Festival',
  createdAt: '2023-11-20T11:45:00',
  totalAmount: 4500.0,
  currency: getTenant().currency.code,
  totalItems: 150,
  completedItems: 127,
  status: 'completed',
  collectionRate: 84.7
}, {
  id: '4',
  name: 'Membership Renewals',
  createdAt: '2023-12-18T16:20:00',
  totalAmount: 3600.0,
  currency: getTenant().currency.code,
  totalItems: 12,
  completedItems: 5,
  status: 'processing',
  collectionRate: 41.7
}, {
  id: '5',
  name: 'Workshop Registrations',
  createdAt: '2023-12-10T08:30:00',
  totalAmount: 2250.0,
  currency: getTenant().currency.code,
  totalItems: 15,
  completedItems: 15,
  status: 'completed',
  collectionRate: 100.0
}, {
  id: '6',
  name: 'Vendor Payments - Nov',
  createdAt: '2023-11-28T10:00:00',
  totalAmount: 45000.0,
  currency: getTenant().currency.code,
  totalItems: 8,
  completedItems: 8,
  status: 'completed',
  collectionRate: 100.0
}, {
  id: '7',
  name: 'Monthly Billing - Group A',
  createdAt: '2023-12-05T09:00:00',
  totalAmount: 15600.5,
  currency: getTenant().currency.code,
  totalItems: 60,
  completedItems: 45,
  status: 'completed',
  collectionRate: 75.0
}, {
  id: '8',
  name: 'Monthly Billing - Group B',
  createdAt: '2023-12-06T09:00:00',
  totalAmount: 18200.25,
  currency: getTenant().currency.code,
  totalItems: 75,
  completedItems: 20,
  status: 'processing',
  collectionRate: 26.6
}, {
  id: '9',
  name: 'Failed Transactions Retry',
  createdAt: '2023-12-19T14:30:00',
  totalAmount: 3450.0,
  currency: getTenant().currency.code,
  totalItems: 14,
  completedItems: 0,
  status: 'failed',
  collectionRate: 0.0
}, {
  id: '10',
  name: 'Holiday Special Promo',
  createdAt: '2023-12-20T08:00:00',
  totalAmount: 55000.0,
  currency: getTenant().currency.code,
  totalItems: 200,
  completedItems: 15,
  status: 'processing',
  collectionRate: 7.5
}, {
  id: '11',
  name: 'January Pre-orders',
  createdAt: '2023-12-21T11:15:00',
  totalAmount: 12500.0,
  currency: getTenant().currency.code,
  totalItems: 50,
  completedItems: 0,
  status: 'draft',
  collectionRate: 0.0
}, {
  id: '12',
  name: 'Consulting Fees - Q4',
  createdAt: '2023-12-14T16:45:00',
  totalAmount: 28000.0,
  currency: getTenant().currency.code,
  totalItems: 5,
  completedItems: 4,
  status: 'completed',
  collectionRate: 80.0
}, {
  id: '13',
  name: 'Equipment Rentals',
  createdAt: '2023-11-15T10:30:00',
  totalAmount: 6750.0,
  currency: getTenant().currency.code,
  totalItems: 22,
  completedItems: 22,
  status: 'completed',
  collectionRate: 100.0
}, {
  id: '14',
  name: 'Software Licenses',
  createdAt: '2023-12-02T13:20:00',
  totalAmount: 9900.0,
  currency: getTenant().currency.code,
  totalItems: 33,
  completedItems: 30,
  status: 'completed',
  collectionRate: 90.9
}, {
  id: '15',
  name: 'Maintenance Contracts',
  createdAt: '2023-12-17T09:15:00',
  totalAmount: 14200.0,
  currency: getTenant().currency.code,
  totalItems: 18,
  completedItems: 5,
  status: 'processing',
  collectionRate: 27.7
}, {
  id: '16',
  name: 'Partner Commissions',
  createdAt: '2023-11-30T15:00:00',
  totalAmount: 21500.0,
  currency: getTenant().currency.code,
  totalItems: 40,
  completedItems: 40,
  status: 'completed',
  collectionRate: 100.0
}, {
  id: '17',
  name: 'Training Sessions',
  createdAt: '2023-12-08T11:00:00',
  totalAmount: 4800.0,
  currency: getTenant().currency.code,
  totalItems: 16,
  completedItems: 16,
  status: 'completed',
  collectionRate: 100.0
}];
export function BulkPayments() {
  const {
    t
  } = useTranslation('payments');
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<keyof BulkPaymentBatch>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const handleSort = (field: keyof BulkPaymentBatch) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const sortedBatches = useMemo(() => {
    return [...MOCK_BATCHES].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      if (sortField === 'createdAt') {
        aValue = new Date(a.createdAt).getTime();
        bValue = new Date(b.createdAt).getTime();
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortField, sortDirection]);
  const totalPages = Math.ceil(sortedBatches.length / itemsPerPage);
  const paginatedBatches = sortedBatches.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };
  const formatAmount = (amount: number) => {
    return formatCurrencyAmount(amount, {
      fractionDigits: 2
    });
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700" data-id="element-8691">
            {t('components.getStatusBadge.completed')}
          </span>;
      case 'processing':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700" data-id="element-8692">
            {t('components.getStatusBadge.processing')}
          </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700" data-id="element-8693">
            {t('components.getStatusBadge.failed')}
          </span>;
      case 'draft':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700" data-id="element-8694">
            {t('pages.getStatusBadge.draft')}
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700 capitalize" data-id="element-8695">
            {status}
          </span>;
    }
  };
  const SortIcon = ({
    field
  }: {
    field: keyof BulkPaymentBatch;
  }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon size={14} className="ml-1 text-gray-500" data-id="element-8696" /> : <ChevronDownIcon size={14} className="ml-1 text-gray-500" data-id="element-8697" />;
  };
  return <div className="space-y-6 pb-12" data-id="element-8698">
      {/* Back Link */}
      <button onClick={() => navigate('/payment-requests?tab=bulk')} className="flex items-center text-[#9F140F] hover:text-[#5C0C08] text-sm font-medium transition-colors" data-id="element-8699">
        <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-8700" />
        {t('pages.BulkPaymentDetail.back_to_payment_form')}
      </button>

      {/* Header */}
      <div data-id="element-8701">
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-8702">
          {t('pages.BulkPayments.all_batches')}
        </h2>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-8703">
        {/* Table Header */}
        <div className="flex items-center px-6 py-3 bg-gray-50 border-b border-gray-200" data-id="element-8704">
          <div className="flex-1 min-w-0 flex items-center cursor-pointer group" onClick={() => handleSort('name')} data-id="element-8705">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider group-hover:text-gray-700 transition-colors" data-id="element-8706">
              {t('components.BulkPaymentList.batch_name')}
            </span>
            <SortIcon field="name" data-id="element-8707" />
          </div>
          <div className="w-36 flex items-center justify-end cursor-pointer group" onClick={() => handleSort('totalAmount')} data-id="element-8708">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider group-hover:text-gray-700 transition-colors" data-id="element-8709">
              {t('components.BulkPaymentPreview.amount')}
            </span>
            <SortIcon field="totalAmount" data-id="element-8710" />
          </div>
          <div className="w-28 flex items-center justify-center cursor-pointer group" onClick={() => handleSort('status')} data-id="element-8711">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider group-hover:text-gray-700 transition-colors" data-id="element-8712">
              {t('components.BulkPaymentList.status')}
            </span>
            <SortIcon field="status" data-id="element-8713" />
          </div>
          <div className="w-32 flex items-center justify-end cursor-pointer group" onClick={() => handleSort('createdAt')} data-id="element-8714">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider group-hover:text-gray-700 transition-colors" data-id="element-8715">
              {t('pages.BulkPayments.date_created')}
            </span>
            <SortIcon field="createdAt" data-id="element-8716" />
          </div>
          <div className="w-10" data-id="element-8717"></div>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-gray-100" data-id="element-8718">
          {paginatedBatches.map(batch => <div key={batch.id} className="flex items-center py-4 px-6 cursor-pointer hover:bg-gray-50/50 transition-colors" onClick={() => navigate(`/bulk-payment-detail/${batch.id}`)} data-id="element-8719">
              <div className="flex-1 min-w-0" data-id="element-8720">
                <p className="text-sm font-medium text-gray-900 truncate" data-id="element-8721">
                  {batch.name}
                </p>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-8722">
                  {batch.totalItems}{' '}
                  {t('components.BulkPaymentListSimplified.payment_links')}
                </p>
              </div>
              <div className="w-36 text-right" data-id="element-8723">
                <p className="text-sm font-medium text-gray-900" data-id="element-8724">
                  {t('components.BulkPaymentListSimplified.usd')}{' '}
                  {formatAmount(batch.totalAmount)}
                </p>
              </div>
              <div className="w-28 flex justify-center" data-id="element-8725">
                {getStatusBadge(batch.status)}
              </div>
              <div className="w-32 text-right" data-id="element-8726">
                <p className="text-sm text-gray-500" data-id="element-8727">
                  {formatDate(batch.createdAt)}
                </p>
              </div>
              <div className="w-10 flex justify-end" data-id="element-8728">
                <button aria-label="View batch details" className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors" onClick={e => {
              e.stopPropagation();
              navigate(`/bulk-payment-detail/${batch.id}`);
            }} data-id="element-8729">
                  <MoreHorizontalIcon size={16} aria-hidden="true" data-id="element-8730" />
                </button>
              </div>
            </div>)}
          {paginatedBatches.length === 0 && <div className="py-12 text-center text-sm text-gray-500" data-id="element-8731">
              {t('pages.BulkPayments.no_batches_found')}
            </div>}
        </div>

        {/* Pagination */}
        {sortedBatches.length > 0 && <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50" data-id="element-8732">
            <div className="text-sm text-gray-500" data-id="element-8733">
              {t('pages.BulkPayments.showing')}{' '}
              <span className="font-medium text-gray-900" data-id="element-8734">
                {(currentPage - 1) * itemsPerPage + 1}
              </span>{' '}
              {t('pages.BulkPayments.to')}{' '}
              <span className="font-medium text-gray-900" data-id="element-8735">
                {Math.min(currentPage * itemsPerPage, sortedBatches.length)}
              </span>{' '}
              {t('components.BulkPaymentList.of')}{' '}
              <span className="font-medium text-gray-900" data-id="element-8736">
                {sortedBatches.length}
              </span>{' '}
              {t('pages.BulkPayments.batches')}
            </div>
            <div className="flex items-center space-x-2" data-id="element-8737">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1 rounded-md text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-8738">
                <ChevronLeftIcon size={20} data-id="element-8739" />
              </button>
              <div className="flex items-center space-x-1" data-id="element-8740">
                {Array.from({
              length: totalPages
            }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${currentPage === page ? 'bg-white border border-gray-300 text-gray-900 shadow-sm' : 'text-gray-500 hover:bg-gray-200'}`} data-id="element-8741">
                    {page}
                  </button>)}
              </div>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1 rounded-md text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-8742">
                <ChevronRightIcon size={20} data-id="element-8743" />
              </button>
            </div>
          </div>}
      </div>
    </div>;
}